// Hook custom per registrare un click su una proprietà dal sito.
// Nota: uso axios e TypeScript per tipizzare i dati e gestire loading e errori.

import { useState } from 'react';
import axios from 'axios';

// Prendo la base URL dell'API dalle variabili d'ambiente
const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

// Interfaccia per tipizzare i dati del click da inviare
interface ClickData {
    propertyId: number; // ID dell'immobile cliccato
    agencyId: number;   // ID dell'agenzia proprietaria dell'immobile
    clickType: number;  // Tipo di click (es. 1 = visualizzazione dettaglio, 2 = richiesta info)
}

/**
 * useAddClickFromSite
 * Hook per registrare un click di un utente su una proprietà dal sito.
 *
 * @remarks
 * - `addClick(data)` invia il click al backend
 * - `isLoading` è true mentre la chiamata è in corso
 * - `error` contiene il messaggio di errore se la chiamata fallisce
 *
 * @example
 * const { addClick, isLoading, error } = useAddClickFromSite();
 * addClick({ propertyId: 123, agencyId: 45, clickType: 1 });
 */
export function useAddClickFromSite() {
    // Stato per gestire il loading della chiamata API
    const [isLoading, setIsLoading] = useState(false);

    // Stato per gestire eventuali errori
    const [error, setError] = useState<string | null>(null);

    // Funzione per inviare il click al backend
    const addClick = async (data: ClickData) => {
        setIsLoading(true);  // inizio loading
        setError(null);      // resetto errori precedenti

        try {
            // PUT request all'endpoint per registrare il click
            await axios.put(`${API_BASE_URL}/add-click-from-site`, data);
        } catch (error) {
            // Se qualcosa va storto, setto l'errore
            setError("Impossibile aggiungere il click");
            console.error("Errore in useAddClickFromSite:", error);
        } finally {
            // Fine loading, qualunque sia il risultato
            setIsLoading(false);
        }
    };

    // Ritorno della funzione e stati
    return { addClick, isLoading, error };
}

export default useAddClickFromSite;
